/* Input transformations */

import * as moment from 'moment'
import { isEmptyObject, isEmptyNestedObject, unfreeze, nonNull, hasRemoteId, get } from 'utils'
import _ from 'lodash'

export const dateOutput = (date) =>
  date && moment.isMoment(date) ? date.toISOString() : moment(date).toISOString()

export const employeeLinkSetupToInput = (employeeLinkSetup) => {
  return {
    defaultExitAmount: employeeLinkSetup.defaultExitAmount,
    defaultExitDate: dateOutput(employeeLinkSetup.defaultExitDate),
    text1: employeeLinkSetup.text1,
    text2: employeeLinkSetup.text2,
    header1: employeeLinkSetup.header1,
    header2: employeeLinkSetup.header2,
  }
}

export const esopSetupToInput = (esopSetup) => {
  return {
    defaultCompensationPeriodInMonths: esopSetup.defaultCompensationPeriodInMonths,
    exitAmount: esopSetup.exitAmount,
    exitDate: esopSetup.exitDate,
    bonusCompensationLevels: esopSetup.bonusCompensationLevels.map((l) => ({
      name: l.name,
      percentFromSalary: l.percentFromSalary,
    })),
  }
}

export function projectToInput(project) {
  return {
    name: project.name,
    roundingMode: project.roundingMode,
    currencyCode: project.currencyCode,
    simulateAD: project.simulateAD,
  }
}

export function simulationSettingsToInput(simulationSettings) {
  return {
    preMoneyDivergence: simulationSettings.preMoneyDivergence,
    dateDivergence: simulationSettings.dateDivergence,
    investmentSumPct: simulationSettings.investmentSumPct,
    exitAmountDivergence: simulationSettings.exitAmountDivergence,
  }
}

export function payOutToInput(payOut) {
  return {
    id: hasRemoteId(payOut) ? payOut.id : null,
    name: payOut.name,
    seniority: payOut.seniority,
    amount: payOut.amount,
    distributionTable: payOut.distributionTable.map((d) => ({
      id: d.investor.id,
      amount: d.amount,
    })),
  }
}

export function buyOutToInput(buyOut) {
  return {
    buyOutId: hasRemoteId(buyOut) ? buyOut.id : null,
    type: 'BUY_OUT',
    roundId: buyOut.round.id,
    buyerId: buyOut.buyer.id,
    sellerId: buyOut.seller.id,
    amount: buyOut.amount,
    pricePerShare: buyOut.pricePerShare,
    buyingShareId: buyOut.buyingShare.id,
    sellingShareId: buyOut.sellingShare.id,
    vestingInput: buyOut.vestingSchedule ? vestingScheduleToInput(buyOut.vestingSchedule) : null,
  }
}

export function manualSharesToInput(manualShares) {
  return {
    id: hasRemoteId(manualShares) ? manualShares.id : null,
    roundId: manualShares.round.id,
    investorId: manualShares.investor.id,
    shareId: manualShares.share.id,
    name: manualShares.name,
    investment: manualShares.investment,
    price: manualShares.price,
    amountOfShares: manualShares.amountOfShares,
    isVirtualBacking: manualShares.isVirtualBacking,
    notes: manualShares.notes,
    date: dateOutput(manualShares.date),
  }
}

export function investorToInput(investor) {
  return {
    type: investor.type,
    name: investor.name,
    investorId: investor.investorId || (hasRemoteId(investor) ? investor.id : null),
    email: _.get(investor, 'email', ''),
    jobTitle: investor.jobTitle,
    salary: investor.salary,
  }
}

export const esopGrantToInput = (grant) => ({
  esopGrantId: hasRemoteId(grant) ? grant.id : null,
  esopId: grant.esop.id,
  investorId: grant.receiver.id,
  roundId: grant.round.id,
  shareAmount: grant.shareAmount,
  date: dateOutput(grant.date),
  expirationDate: grant.expirationDate,
  strikePrice: grant.strikePrice,
  vestingInput: grant.vestingSchedule
    ? vestingScheduleToInput(grant.vestingSchedule, grant.date)
    : null,
  packageCalculationInput: grant.packageCalculation
    ? {
        packageValue: grant.packageCalculation.packageValue,
        salaryCompensation: grant.packageCalculation.salaryCompensation,
        bonusCompensation: grant.packageCalculation.bonusCompensation,
        compensationPeriodInMonths: grant.packageCalculation.compensationPeriodInMonths,
        percentOfPackageBackedByThisGrant:
          grant.packageCalculation.percentOfPackageBackedByThisGrant,
      }
    : null,
})

export function esopPoolToInput(pool) {
  return {
    name: pool.esop.name,
    roundId: pool.round.id,
    notes: pool.esop.notes,
    dilutionType: pool.esop.dilutionType,
    valuationType: pool.valuationType,
    shareAmount: pool.shareAmount,
    backingTable:
      pool.backingTable && pool.esop.dilutionType === 'BY_BACKING'
        ? pool.backingTable.map((v) => ({
            investorId: v.investor.id,
            numberOfShares: v.numberOfShares,
          }))
        : null,
    defaultVestingInput: nonNull(pool.defaultVestingSchedule)
      ? vestingScheduleToInput(pool.defaultVestingSchedule)
      : null,
  }
}

export function cnToInput(cn, isConversion) {
  return isConversion
    ? { id: cn.id }
    : {
        dateOfDeposit: dateOutput(cn.dateOfDeposit),
        maturityDate: dateOutput(cn.maturityDate),
        shareId: cn.share ? cn.share.id : null,
        discount: cn.discount,
        preMoneyCap: cn.preMoneyCap,
        preMoneyFloor: cn.preMoneyFloor,
        forcedPaymentOnConversion: cn.forcedPaymentOnConversion,
        defaultPriceCalculationMethod: cn.defaultPriceCalculationMethod,
        interestRate: cn.interestRate
          ? {
              interestRate: cn.interestRate.interestRate,
              cumulative: cn.interestRate.cumulative,
              accumulationType: cn.interestRate.accumulationType,
              retainDividends: cn.interestRate.retainDividends,
              limitInMonths: cn.interestRate.limitInMonths,
              limitAsDate: cn.interestRate.limitAsDate,
            }
          : null,
      }
}

export function companyToInput(company) {
  return {
    name: company.name,
    companyURL: company.companyURL,
    companyLogoURL: company.companyLogoURL,
    sector: company.sector,
    location: company.location,
    exist: company.exist,
    invest: company.invest,
    districtCourt: company.districtCourt,
    registrationNumber: company.registrationNumber,
    information: company.information,
    address: company.address
      ? {
          street: company.address.street,
          additional: company.address.additional,
          city: company.address.city,
          postcode: company.address.postcode,
          country: company.address.country,
        }
      : null,
  }
}

export function shareToInput(share) {
  return {
    id: hasRemoteId(share) ? share.id : null,
    name: share.name,
    seniority: share.seniority,
    votingRights: share.votingRights,
    text: share.text,
    defaultPriceCalculationMethod: share.defaultPriceCalculationMethod,
    // commonShare: share.commonShare, -> can not be modified in backend (only one predefined share)
    liquidationPreference: share.liquidationPreference
      ? {
          multiple: share.liquidationPreference.multiple,
          simple: share.liquidationPreference.simple,
          agreedConversion: share.liquidationPreference.agreedConversion,
          senior: share.liquidationPreference.senior,
          capType: share.liquidationPreference.capType,
          cap: share.liquidationPreference.cap,
          customSharePrice: share.liquidationPreference.customSharePrice,
          interestRate: share.liquidationPreference.interestRate
            ? {
                interestRate: share.liquidationPreference.interestRate.interestRate,
                cumulative: share.liquidationPreference.interestRate.cumulative,
                accumulationType: share.liquidationPreference.interestRate.accumulationType,
                retainDividends: share.liquidationPreference.interestRate.retainDividends,
                limitInMonths: share.liquidationPreference.interestRate.limitInMonths,
              }
            : null,
        }
      : null,
    antiDilution: share.antiDilution
      ? {
          type: share.antiDilution.type,
          compensation: share.antiDilution.compensation,
          duration: share.antiDilution.duration,
          transferType: share.antiDilution.transferType,
        }
      : null,
  }
}

export function roundToInput(round) {
  return {
    name: round.name,
    type: round.type,
    cashIn: round.cashIn,
    date: dateOutput(round.date),
    preMoneyValuation: parseFloat(round.preMoneyValuation),
    shareId: round.share.id,
  }
}

export function investmentToInput(investment) {
  return {
    investmentId: hasRemoteId(investment) ? investment.id : null,
    amount: parseFloat(investment.amount),
    interestAmount: investment.interestAmount ? parseFloat(investment.interestAmount) : null,
    type: investment.type,
    investorId: investment.investor.id,
    roundId: investment.round.id,
    pricePerShare: investment.pricePerShare,
    amountOfShares: investment.amountOfShares,
    date: investment.date ? dateOutput(investment.date) : null,
    shareId: investment.share ? investment.share.id : null,
    cnInput: investment.convertibleNote
      ? cnToInput(investment.convertibleNote, investment.type === 'CN_CONVERSION')
      : null,
    vestingInput: investment.vestingSchedule
      ? vestingScheduleToInput(investment.vestingSchedule)
      : null,
  }
}

export function vestingScheduleToInput(vestingSchedule, eventDate) {
  return {
    accelerationOnExit: vestingSchedule.accelerationOnExit,
    startDate: dateOutput(vestingSchedule.startDate || eventDate),
    percentUnderlyingVesting: vestingSchedule.percentUnderlyingVesting,
    cliff: vestingSchedule.cliff,
    cliffPercent: vestingSchedule.cliffPercent,
    schedule: vestingSchedule.schedule.map((s) => ({
      percent: s.percent,
      months: s.months,
      vestingTime: s.vestingTime,
    })),
  }
}

export function addressToInput(address) {
  return {
    street: address.street,
    postcode: address.postcode,
    city: address.city,
    country: address.country,
    additional: address.additional,
  }
}

export function businessToInput(business) {
  return {
    name: business.name || '',
    companyLogoURL: business.companyLogoURL,
    address: !isEmptyNestedObject(business.address) ? addressToInput(business.address) : null,
  }
}

export function userToInput(user) {
  const {
    userId,
    email,
    firstname,
    lastname,
    title,
    avatarURL,
    newsSubscription,
    agreedLatestAGB,
    signupTime,
    lastLogin,
    numberOfLogins,
    business,
    userStatus,
    expertType,
  } = user

  return {
    userId,
    email,
    firstname,
    lastname,
    title,
    avatarURL,
    newsSubscription,
    agreedLatestAGB,
    signupTime,
    lastLogin,
    lastProfileUpdate: dateOutput(new Date()),
    numberOfLogins,
    business: !isEmptyNestedObject(business) ? businessToInput(business) : null,
    userStatus,
    expertType,
  }
}

export const superFormToInput = (superNodeForm, investmentsTable, investors) => {
  const superNode = unfreeze(superNodeForm) // deep copy to not modify original object
  const numberToRange = (n) => ({ start: n || 0, end: 0, stepSize: 0 })

  // ---------------------------------

  // Updating the investments based on investmentsTable
  const investmentsByInvestor = investmentsTable.map((i) => {
    let { investorId } = i
    const investor = investors.find((investor) => investor.id === investorId)
    let investorName
    if (!investor) {
      investorName = i.investorId // this is created
      investorId = null
    } else investorName = investor.name

    // NOTE converting to percentage (to be flexible for totalInvestmentAmount as range if needed)
    const cnPercent = i.cn / superNode.totalInvestmentAmount
    const equityPercent = i.equity / superNode.totalInvestmentAmount

    return {
      cnPercent,
      equityPercent,
      investorId,
      investorName,
    }
  })

  superNode.investmentsByInvestor = investmentsByInvestor
  superNode.totalInvestmentAmount = numberToRange(superNode.totalInvestmentAmount)

  if (_.get(superNode, 'cn.ir')) {
    _.set(superNode, 'cn.ir.limitInMonths', numberToRange(superNode.cn.ir.limitInMonths))
  }

  if (_.get(superNode, 'equity.lp.ir')) {
    _.set(
      superNode,
      'equity.lp.ir.limitInMonths',
      numberToRange(superNode.equity.lp.ir.limitInMonths),
    )
  }

  return superNodeToInput(superNode)
}

export const superNodeToInput = (superNode) => {
  // ---------------------------------
  // Helper functions
  // ---------------------------------
  const undefinedObject = (object) =>
    object === null || typeof object === 'undefined' || isEmptyObject(object)

  const toRange = (range) =>
    undefinedObject(range)
      ? { start: 0, end: 0, stepSize: 0 }
      : { start: range.start || 0, end: range.end || 0, stepSize: range.stepSize || 0 }

  const input = {
    name: superNode.name,
    totalInvestmentAmount: toRange(superNode.totalInvestmentAmount),
    roundDate: dateOutput(superNode.roundDate),
    esopPoolSize: toRange(superNode.esopPoolSize),
    investmentsByInvestor: superNode.investmentsByInvestor.map((i) => ({
      cnPercent: i.cnPercent,
      equityPercent: i.equityPercent,
      investorId: i.investorId,
      investorName: i.investorName,
    })),
  }

  if (!undefinedObject(superNode.cn)) {
    input.cn = {
      // Currently dates are not used (!) thus simply use round date
      dateOfDeposit: dateOutput(superNode.roundDate), // dateOutput(superNode.cn.dateOfDeposit),
      maturityDate: dateOutput(superNode.roundDate), // dateOutput(superNode.cn.maturityDate),
      discount: toRange(superNode.cn.discount),
      preMoneyCap: toRange(superNode.cn.preMoneyCap),
    }

    if (!undefinedObject(superNode.cn.ir)) {
      input.cn.ir = {
        interestRate: toRange(superNode.cn.ir.interestRate),
        cumulative: superNode.cn.ir.cumulative,
        accumulationType: superNode.cn.ir.accumulationType,
        limitInMonths: toRange(superNode.cn.ir.limitInMonths),
      }
    }
  }

  if (!undefinedObject(superNode.equity)) {
    input.equity = {
      preMoneyValuation: toRange(superNode.equity.preMoneyValuation),
    }

    if (!undefinedObject(superNode.equity.lp)) {
      const cap = superNode.equity.lp.capType === 'NONE' ? null : superNode.equity.lp.cap
      input.equity.lp = {
        multiple: toRange(superNode.equity.lp.multiple),
        simple: superNode.equity.lp.simple,
        agreedConversion: superNode.equity.lp.agreedConversion,
        capType: superNode.equity.lp.capType,
        cap: toRange(cap),
      }
      if (!undefinedObject(superNode.equity.lp.ir)) {
        input.equity.lp.ir = {
          interestRate: toRange(superNode.equity.lp.ir.interestRate),
          cumulative: superNode.equity.lp.ir.cumulative,
          accumulationType: superNode.equity.lp.ir.accumulationType,
          limitInMonths: toRange(superNode.equity.lp.ir.limitInMonths),
        }
      }
    }

    if (!undefinedObject(superNode.equity.ad)) {
      input.equity.ad = {
        type: superNode.equity.ad.type,
        compensation: superNode.equity.ad.compensation,
        duration: superNode.equity.ad.duration,
        transferType: superNode.equity.ad.transferType,
      }
    }
  }

  return input
}

// ---------------------------------------------------------------------------------------------

// Workaround functions to set nested objects null if their fields are set to undefined (by antd-form)
// Required because rendering the antd fields automatically causes the object to be initialized
// Only if an <optional> <nested> object is part of the form (e.g. lp, ad, ir, vesting-schedule)

export function shareFromForm(formValues, share) {
  return {
    ...share,
    ...formValues,
    liquidationPreference:
      get('liquidationPreference.multiple', formValues, null) !== null
        ? {
            ...get('liquidationPreference', share, {}),
            ...formValues.liquidationPreference,
            interestRate:
              get('liquidationPreference.interestRate.interestRate', formValues, null) !== null
                ? {
                    ...get('liquidationPreference.interestRate', share, {}),
                    ...formValues.liquidationPreference.interestRate,
                  }
                : null,
          }
        : null,
    antiDilution:
      get('antiDilution.type', formValues, null) !== null
        ? {
            ...get('antiDilution', share, {}),
            ...formValues.antiDilution,
          }
        : null,
  }
}

export function esopFromForm(formValues, esopEvent) {
  return {
    ...esopEvent,
    ...formValues,
    esop: {
      ...get('esop', esopEvent, {}),
      ...formValues.esop,
    },
    defaultVestingSchedule:
      get('defaultVestingSchedule.accelerationOnExit', formValues, null) !== null
        ? {
            ...get('defaultVestingSchedule', esopEvent, {}),
            ...formValues.defaultVestingSchedule,
          }
        : null,
  }
}

export function esopGrantFromForm(formValues, esopGrant) {
  return {
    ...esopGrant,
    ...formValues,
    vestingSchedule:
      get('vestingSchedule.accelerationOnExit', formValues, null) !== null
        ? {
            ...get('vestingSchedule', esopGrant, {}),
            ...formValues.vestingSchedule,
          }
        : null,
  }
}

export function vestingEventFromForm(formValues, vestingEvent) {
  return {
    ...vestingEvent,
    ...formValues,
    esop: {
      ...get('esop', vestingEvent, {}),
      ...get('esop', formValues, {}),
    },
    vestingSchedule:
      get('vestingSchedule.accelerationOnExit', formValues, null) !== null
        ? {
            ...get('vestingSchedule', vestingEvent, {}),
            ...formValues.vestingSchedule,
          }
        : null,
  }
}
